import { ok, err, Result } from 'neverthrow';
import type { Conversation } from '@cotera/sdk/core';
import type { CredentialInfo } from './ask-for-credentials-renderer';
import type { Strategy } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { toast } from '@cotera/client/app/components';

export type SubmitCredentialsInput = {
  /** The main tool call ID to submit */
  toolCallId: string;
  /** The provider tool key (e.g., "hubspot/tools") */
  toolKey: string;
  /** The credential info to include in the result */
  credentialInfo: CredentialInfo;
  /** Success message to include in the result */
  successMessage: string;
  /** Whether this is a success or skip result */
  success: boolean;
  /** Conversation to scan for related incomplete credential requests */
  conversation: Conversation | null;
  /** API client for submitting tool results */
  client: TenantedClient;
  /** Chat model info */
  conversationId: string;
  model: string;
};

/**
 * Finds all incomplete ask-for-credentials tool calls for the same provider.
 * Returns a set of tool call IDs including the main one.
 */
export function findRelatedCredentialToolCalls(
  conversation: Conversation | null,
  mainToolCallId: string
): Set<string> {
  const toolCallIds = new Set<string>([mainToolCallId]);

  if (conversation === null) {
    return toolCallIds;
  }

  const mainToolCall = conversation.findToolCall(mainToolCallId);

  if (mainToolCall === null) {
    return toolCallIds;
  }

  const credentialInfo = mainToolCall.getOutput<{
    credentialInfo: {
      provider: string;
      strategies: {
        strategy: Strategy;
      }[];
    };
  }>()?.credentialInfo;

  for (const message of conversation.getAllMessages()) {
    if (!message.isAssistantMessage()) {
      continue;
    }

    const credentialRequestParts = message
      .getCredentialRequestParts()
      .filter((p) => p.isIncomplete());

    for (const part of credentialRequestParts) {
      const output = part.getOutput<{
        credentialInfo: {
          provider: string;
          strategies: {
            strategy: Strategy;
          }[];
        };
      }>();
      if (
        output?.credentialInfo?.provider === credentialInfo?.provider &&
        output?.credentialInfo?.strategies
          .map((s) => JSON.stringify(s.strategy))
          .join('|') ===
          credentialInfo?.strategies
            .map((s) => JSON.stringify(s.strategy))
            .join('|')
      ) {
        toolCallIds.add(part.toolCallId);
      }
    }
  }

  return toolCallIds;
}

/**
 * Submit credential results for a tool call and all related incomplete
 * credential requests for the same provider.
 */
export async function submitCredentialsAction(
  input: SubmitCredentialsInput
): Promise<Result<void, string>> {
  const {
    toolCallId,
    credentialInfo,
    successMessage,
    success,
    conversation,
    client,
    conversationId,
    model,
  } = input;

  // Find all related tool calls
  const toolCallIdsToSubmit = findRelatedCredentialToolCalls(
    conversation,
    toolCallId
  );

  const result = await client.resource.chat.submitTool({
    toolCallIds: Array.from(toolCallIdsToSubmit),
    conversationId,
    model,
    result: {
      credentialInfo,
      success,
      message: successMessage,
    },
  });

  if (result.isErr()) {
    toast.error(
      result.error.t === 'application'
        ? result.error.err.message
        : 'Failed to submit credentials'
    );
    return err(JSON.stringify(result.error));
  }

  return ok(undefined);
}
